import React, { useCallback } from 'react';
import { FieldLabel } from '@puckeditor/core';
import type { CustomField } from '@puckeditor/core';
import styled from 'styled-components';

import { brandColors } from 'src/theme/constants/brand-colors';

type ToggleFieldProps = {
  field: { label?: string };
  name: string;
  value: unknown;
  onChange: (value: boolean) => void;
  readOnly?: boolean;
};

const InlineField = styled.div`
  > label {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    column-gap: 12px;
  }

  /* Puck renders the label text (and read-only icon) in its first child. */
  > label > div:first-child {
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
    padding-bottom: 0;
  }
`;

const Switch = styled.button`
  flex-shrink: 0;
  display: block;
  position: relative;
  box-sizing: border-box;
  width: var(--dcm-cms-toggle-width, 40px);
  height: var(--dcm-cms-toggle-height, 24px);
  padding: var(--dcm-cms-toggle-inset, 3px);
  border: var(--dcm-cms-toggle-border, 1px solid transparent);
  border-radius: var(--dcm-cms-toggle-radius, 999px);
  background: var(--dcm-cms-toggle-off-background, #646d72);
  cursor: pointer;

  &[aria-checked='true'] {
    background: var(--dcm-cms-toggle-on-background, ${brandColors.moss});
  }

  &:focus-visible {
    outline: var(--dcm-cms-toggle-focus-outline, 3px solid #0b99a8);
    outline-offset: var(--dcm-cms-toggle-focus-offset, 2px);
  }

  &:disabled {
    opacity: var(--dcm-cms-toggle-disabled-opacity, 0.5);
    cursor: not-allowed;
  }

  &::after {
    content: '';
    display: block;
    width: var(--dcm-cms-toggle-thumb-size, 16px);
    height: var(--dcm-cms-toggle-thumb-size, 16px);
    border-radius: var(--dcm-cms-toggle-thumb-radius, 999px);
    background: var(--dcm-cms-toggle-thumb-background, #fff);
  }

  &[aria-checked='true']::after {
    margin-left: auto;
  }
`;

const Hint = styled.p`
  flex-basis: 100%;
  min-width: 0;
  margin: 4px 0 0;
  font-size: 12px;
  color: var(--dcm-cms-field-hint-color, #646d72);
`;

/**
 * `defaultOn` mirrors renderers that treat every value except an explicit `false` as on.
 * `inline` puts the label and switch on one row (component-sidebar toggles); page-level
 * controls (Header/Navigation Show/Hide) opt out and keep Puck's default stacked layout.
 */
function ToggleField(hint?: string, defaultOn = false, inline = true) {
  return function ToggleFieldWidget({ field, name, value, onChange, readOnly }: ToggleFieldProps): JSX.Element {
    const checked = defaultOn ? value !== false : value === true;
    const handleClick = useCallback(() => onChange(!checked), [checked, onChange]);
    const hintId = hint ? `toggle-hint-${name}` : undefined;

    const body = (
      <FieldLabel label={field.label ?? name} readOnly={readOnly}>
        <Switch
          type='button'
          role='switch'
          aria-label={field.label ?? name}
          aria-checked={checked}
          aria-describedby={hintId}
          disabled={readOnly}
          onClick={handleClick}
          data-testid={`toggle-${name}`}
        />
        {hint && (
          <Hint id={hintId} data-testid={`toggle-hint-${name}`}>
            {hint}
          </Hint>
        )}
      </FieldLabel>
    );

    return inline ? <InlineField>{body}</InlineField> : body;
  };
}

/** An on/off switch whose inspector value and AI schema both use JSON booleans. */
export function toggleField(
  label: string,
  aiInstructions?: string,
  hint?: string,
  options: { defaultOn?: boolean; inline?: boolean } = {}
): CustomField<boolean> {
  return {
    type: 'custom',
    label,
    render: ToggleField(hint, options.defaultOn, options.inline ?? true),
    ai: {
      schema: { type: 'boolean' },
      ...(aiInstructions ? { instructions: aiInstructions } : {}),
    },
  };
}
